'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { QrCode, ScanLine, CheckCircle2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import type { SortingSessionItem, SortingPageScan } from '../types';

interface SortingScanLogProps {
  items: SortingSessionItem[];
  isScanning: boolean;
  activeContainer: SortingSessionItem | null;
  selectedItem: SortingSessionItem | null;
  onAcceptContainer: () => void;
  isAccepting: boolean;
  className?: string;
}

interface PageRow {
  page_number: number;
  status: 'success' | 'pending';
  scanned_at: string | null;
  scan?: SortingPageScan;
}

export function SortingScanLog({
  isScanning,
  activeContainer,
  selectedItem,
  onAcceptContainer,
  isAccepting,
  className
}: SortingScanLogProps) {
  const t = useTranslations('sorting');

  const displayContainer = selectedItem;
  const totalPages = displayContainer?.registration?.expected_page_count ?? 0;
  const containerAccepted = displayContainer?.status === 'scanned';
  const canAccept =
    isScanning &&
    !!displayContainer &&
    displayContainer.status !== 'scanned' &&
    displayContainer.status !== 'refused';

  const pageRows = useMemo<PageRow[]>(() => {
    if (!displayContainer) return [];

    const scans = displayContainer.page_scans ?? [];
    const scannedSet = new Map<number, SortingPageScan>();
    for (const scan of scans) scannedSet.set(scan.page_number, scan);

    const rows: PageRow[] = [];

    const sortedScans = [...scans].sort(
      (a, b) =>
        new Date(b.scanned_at).getTime() - new Date(a.scanned_at).getTime()
    );
    for (const scan of sortedScans) {
      rows.push({
        page_number: scan.page_number,
        status: 'success',
        scanned_at: scan.scanned_at,
        scan
      });
    }

    for (let i = 1; i <= totalPages; i++) {
      if (!scannedSet.has(i)) {
        rows.push({
          page_number: i,
          status: containerAccepted ? 'success' : 'pending',
          scanned_at: null
        });
      }
    }

    return rows;
  }, [displayContainer, totalPages, containerAccepted]);

  return (
    <Card className={cn('flex flex-col lg:h-full', className)}>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <CardTitle className='flex items-center gap-2 text-base'>
            <ScanLine className='h-4 w-4' />
            {t('scanLog')}
          </CardTitle>
          {displayContainer && (
            <Badge
              variant='secondary'
              className={cn(
                activeContainer?.id === displayContainer.id &&
                  'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              )}
            >
              {displayContainer.container_number}
            </Badge>
          )}
        </div>
        {canAccept && (
          <Button
            size='sm'
            className='mt-2 w-full bg-green-600 text-white hover:bg-green-700'
            onClick={onAcceptContainer}
            disabled={isAccepting}
          >
            <CheckCircle2 className='me-2 h-4 w-4' />
            {t('scanAllPages')}
          </Button>
        )}
      </CardHeader>

      <CardContent className='flex flex-col p-0 lg:min-h-0 lg:flex-1'>
        {!displayContainer ? (
          <div className='flex flex-1 flex-col items-center justify-center py-8'>
            <div className='bg-primary/10 mb-4 rounded-full p-4'>
              <QrCode className='text-primary h-10 w-10 animate-pulse' />
            </div>
            <p className='text-base font-semibold'>{t('awaitingScan')}</p>
            <p className='text-muted-foreground mt-1 text-sm'>
              {t('awaitingScanHint')}
            </p>
          </div>
        ) : (
          <>
            <div className='shrink-0 border-b px-6 py-3'>
              <p className='text-muted-foreground text-center text-xs font-medium tracking-wide uppercase'>
                {t('qrScanLogs')}
              </p>
            </div>

            <div className='max-h-72 overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1'>
              <table className='w-full caption-bottom text-sm'>
                <TableHeader className='bg-muted/95 sticky top-0 z-10'>
                  <TableRow>
                    <TableHead className='text-xs'>{t('pageNumber')}</TableHead>
                    <TableHead className='text-xs'>{t('timestamp')}</TableHead>
                    <TableHead className='text-xs'>{t('scanStatus')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((row) => (
                    <TableRow
                      key={row.page_number}
                      className='hover:bg-muted/30'
                    >
                      <TableCell className='py-2 font-mono text-xs'>
                        {displayContainer.container_number}-P{row.page_number}
                      </TableCell>
                      <TableCell className='text-muted-foreground py-2 text-xs'>
                        {row.scanned_at
                          ? new Date(row.scanned_at).toLocaleTimeString()
                          : '—'}
                      </TableCell>
                      <TableCell className='py-2'>
                        {row.status === 'success' ? (
                          <Badge
                            variant='outline'
                            className='border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400'
                          >
                            <CheckCircle2 className='me-1 h-3 w-3' />
                            {t('success')}
                          </Badge>
                        ) : (
                          <Badge
                            variant='outline'
                            className='border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                          >
                            <Clock className='me-1 h-3 w-3' />
                            {t('scanPending')}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
